import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { ConvexQueryClient } from '@convex-dev/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { GoogleAnalytics } from './lib/analytics/google-analytics';
import { PostHogProvider } from './lib/analytics/posthog';
// Generated Routes
import { routeTree } from './routeTree.gen';
// Styles
import '@repo/ui/globals.css';

// Import your Publishable Key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

if (!PUBLISHABLE_KEY) {
    throw new Error('Add your VITE_CLERK_PUBLISHABLE_KEY to the .env file');
}

const CONVEX_URL = import.meta.env.VITE_CONVEX_URL as string;

if (!CONVEX_URL) {
    throw new Error('Add your VITE_CONVEX_URL to the .env file');
}

const convex = new ConvexReactClient(CONVEX_URL);

const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            queryKeyHashFn: convexQueryClient.hashFn(),
            queryFn: convexQueryClient.queryFn(),
        },
    },
});
convexQueryClient.connect(queryClient);

// Create a new router instance
const router = createRouter({
    routeTree,
    context: {
        queryClient,
    },
    scrollRestoration: true,
    defaultPreload: 'intent', // Preload routes on hover/touch
    defaultPreloadStaleTime: 0, // Let TanStack Query handle staleness
    defaultPreloadDelay: 100,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

export const App = () => {
    return (
        <ClerkProvider
            publishableKey={PUBLISHABLE_KEY}
            appearance={{
                baseTheme: dark,
                elements: {
                    profileSection__danger: {
                        display: 'none',
                    },
                },
            }}
            signInFallbackRedirectUrl={'/'}
            signUpFallbackRedirectUrl={'/'}
            signInForceRedirectUrl={'/'}
            signUpForceRedirectUrl={'/'}>
            <GoogleAnalytics />
            <PostHogProvider>
                <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
                    <QueryClientProvider client={queryClient}>
                        <RouterProvider router={router} />
                    </QueryClientProvider>
                </ConvexProviderWithClerk>
            </PostHogProvider>
        </ClerkProvider>
    );
};

// Render the app
const rootElement = document.getElementById('root');
if (!rootElement) {
    throw new Error('Failed to mount React app: missing #root element in index.html');
}
if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
        <StrictMode>
            <App />
        </StrictMode>
    );
}
